import KeyAlerts from '../components/KeyAlerts';
import KeyBudgetModal from '../components/KeyBudgetModal';
import KeyLimitField, { defaultKeyLimits, readKeyLimits, validKeyLimits } from '../components/KeyLimitField';
import KeyGroupsModal from '../components/KeyGroupsModal';
import KeyActivityModal from '../components/KeyActivityModal';
import KeyBatchBar from '../components/KeyBatchBar';
import { useState, useEffect, useCallback } from 'react';
import { App as AntApp, Table, Tag, Button, Space, Input, Modal, message, Card, Statistic, Row, Col } from 'antd';
import { ReloadOutlined, SearchOutlined, SwapOutlined, UserOutlined } from '@ant-design/icons';
import { apiKeyAPI } from '../services/api';
import { useSearchParams } from 'react-router-dom';

const APIKeys = () => {
  const [loading, setLoading] = useState(false);
 const [budgetKey,setBudgetKey]=useState(null);

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [searchEmail, setSearchEmail] = useState('');
  const [searchUserId, setSearchUserId] = useState('');
  const [searchApiKey, setSearchApiKey] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  const [totalUsageAll, setTotalUsageAll] = useState(0);
 const [groupsOpen,setGroupsOpen]=useState(false);
 const [limitKey,setLimitKey]=useState(null),[editLimits,setEditLimits]=useState(defaultKeyLimits),[savingLimits,setSavingLimits]=useState(false);
  const [selectedKeys,setSelectedKeys]=useState([]);const [activityOpen,setActivityOpen]=useState(false);const [activityKey,setActivityKey]=useState(null);

  // 统一的获取数据函数
  const fetchData = useCallback(async (page, pageSize, email, userId, apiKey, sortField, sortOrd) => {
    setLoading(true);
    try {
      const params = {};
      if (email) params.email = email;
      if (userId) params.user_id = userId;
      if (apiKey) params.api_key = apiKey;
      if (sortField) {
        params.sort_by = sortField;
        params.sort_order = sortOrd;
      }

      let response = await apiKeyAPI.getList(page, pageSize, params);
      if (response.code === 0) {
        const lastPage = Math.max(1, Math.ceil(response.data.total / pageSize));
        if (page > lastPage) response = await apiKeyAPI.getList(lastPage, pageSize, params);
      }
      if (response.code === 0) {
        setData(response.data.list || []);
        setPagination({ current: response.data.page, pageSize: response.data.page_size, total: response.data.total });
        // 使用后端返回的总用量（所有符合条件的数据）
        setTotalUsageAll(parseFloat(response.data.total_usage || 0));
      }
    } catch (error) {
      console.error('获取 API Keys 失败:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // 初始化加载
  useEffect(() => {
    const userIdFromUrl = searchParams.get('user_id');
    if (userIdFromUrl) {
      setSearchUserId(userIdFromUrl);
      fetchData(1, 20, '', userIdFromUrl, '', '', '');
    } else {
      fetchData(1, 20, '', '', '', '', '');
    }
  }, []);

  // 搜索
  const handleSearch = () => {
    searchParams.delete('user_id');
    setSearchParams(searchParams);
    fetchData(1, pagination.pageSize, searchEmail, searchUserId, searchApiKey, sortBy, sortOrder);
  };

  // 重置
  const handleReset = () => {
    setSearchEmail('');
    setSearchUserId('');
    setSearchApiKey('');
    setSortBy('');
    setSortOrder('');
    searchParams.delete('user_id');
    setSearchParams(searchParams);
    fetchData(1, pagination.pageSize, '', '', '', '', '');
  };

  // 清除筛选
  const handleClearFilter = () => {
    setSearchUserId('');
    searchParams.delete('user_id');
    setSearchParams(searchParams);
    fetchData(1, pagination.pageSize, searchEmail, '', searchApiKey, sortBy, sortOrder);
  };

  // 表格变化（分页、排序）
  const handleTableChange = (newPagination, filters, sorter) => {
    const newSortBy = sorter.field || '';
    const newSortOrder = sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : '';
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    fetchData(newPagination.current, newPagination.pageSize, searchEmail, searchUserId, searchApiKey, newSortBy, newSortOrder);
  };

  // 切换状态
  const handleToggle = async (id) => {
    try {
      const response = await apiKeyAPI.toggle(id);
      if (response.code === 0) {
        message.success(response.data.is_active ? '已启用' : '已禁用');
        fetchData(pagination.current, pagination.pageSize, searchEmail, searchUserId, searchApiKey, sortBy, sortOrder);
      }
    } catch (error) {
      message.error('操作失败');
    }
  };

  const columns = [
    {title:'周期预算',width:190,render:(_,row)=><Space direction="vertical"><span>日 ¥{row.daily_budget || 0} / 月 ¥{row.monthly_budget || 0}</span><Button type="link" onClick={()=>setBudgetKey(row)}>预算、用量与提醒</Button></Space>},
    {title:'Key 限流',width:220,render:(_,row)=><Space direction="vertical"><span>RPM {row.requests_per_minute || '不限'} · TPM {row.tokens_per_minute || '不限'} · 并发 {row.max_concurrency || '不限'}</span><Button type="link" onClick={()=>{setLimitKey(row);setEditLimits(readKeyLimits(row));}}>修改限流与并发</Button></Space>},
 {title:'渠道分组',width:200,render:(_,row)=>row.route_group==='auto'?(row.auto_groups || []).join(' → '):(row.route_group || 'default')},
 {title:'日志',key:'activity',width:120,render:(_,record)=><Button type="link" onClick={()=>{setActivityKey(record);setActivityOpen(true);}}>日志与统计</Button>},
    { title: 'ID', dataIndex: 'id', key: 'id', width: 280, render: (id) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{id}</span> },
    { title: '用户ID', dataIndex: 'user_id', key: 'user_id', width: 80 },
    { title: '用户邮箱', dataIndex: 'user', key: 'email', width: 200, render: (user) => user?.email || '-' },
    { title: '手机号', dataIndex: 'user', key: 'phone', width: 130, render: (user) => user?.phone || '-' },
    { title: 'Key 名称', dataIndex: 'name', key: 'name', width: 120 },
    { title: 'API Key', dataIndex: 'api_key', key: 'api_key', width: 220, render: (text) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{text ? text.substring(0, 12) + '...' + text.substring(text.length - 6) : '-'}</span> },
    { title: '消费与预扣（人民币）', dataIndex: 'total_usage', key: 'total_usage', width: 130, sorter: true, render: (val,record) => {
      // total_usage 后端已转换为金额
      const usageInYuan = Math.max(0,Number(val || 0)-Number(record.reserved_usage || 0)).toFixed(6);
      return <span style={{ color: val > 0 ? '#f59e0b' : '#999', fontWeight: val > 0 ? 500 : 400 }}>¥ {usageInYuan}<div style={{fontSize:11}}>预扣 ¥ {Number(record.reserved_usage || 0).toFixed(6)}</div></span>;
    }},
    { title: '状态', dataIndex: 'is_active', key: 'is_active', width: 80, render: (isActive) => <Tag color={isActive ? 'green' : 'red'}>{isActive ? '启用' : '禁用'}</Tag> },
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 150, sorter: true, render: (text) => text ? new Date(text).toLocaleString('zh-CN') : '-' },
    { title: '最近使用', dataIndex: 'last_used_at', key: 'last_used_at', width: 150, sorter: true, render: (text) => text ? new Date(text).toLocaleString('zh-CN') : '-' },
    { title: '操作', key: 'actions', width: 80, fixed: 'right', render: (_, record) => <Button type="link" size="small" icon={<SwapOutlined />} onClick={() => handleToggle(record.id)}>{record.is_active ? '禁用' : '启用'}</Button> },
  ];

  const activeKeys = data.filter(d => d.is_active).length;

  return (
    <div className="fit-screen-page">
      {/* 页面标题 + 统计卡片 */}
      <div className="page-header" style={{ borderBottom: 'none', paddingBottom: 0, marginBottom: 8 }}>
        <h2 className="page-title" style={{ marginBottom: 8 }}>API Key 管理</h2>
 <KeyAlerts api={apiKeyAPI} admin />
        <Row gutter={12}>
          <Col span={8}><Card size="small" styles={{ body: { padding: '8px 12px' } }}><Statistic title="Key 总数" value={pagination.total} /></Card></Col>
          <Col span={8}><Card size="small" styles={{ body: { padding: '8px 12px' } }}><Statistic title="本页启用数" value={activeKeys} valueStyle={{ color: '#52c41a' }} /></Card></Col>
          <Col span={8}><Card size="small" styles={{ body: { padding: '8px 12px' } }}><Statistic title="匹配 Key 累计消费" value={totalUsageAll.toFixed(6)} prefix="¥" valueStyle={{ color: totalUsageAll > 0 ? '#f59e0b' : '#999' }} /></Card></Col>
        </Row>
      </div>

      {/* 搜索栏 */}
      <div className="search-bar" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {searchParams.get('user_id') && (
          <Tag closable onClose={handleClearFilter} style={{ marginRight: 8 }}>
            筛选用户: {searchParams.get('user_id')}
          </Tag>
        )}
        <Input placeholder="搜索用户邮箱" value={searchEmail} onChange={(e) => setSearchEmail(e.target.value)} style={{ width: 200 }} allowClear disabled={!!searchParams.get('user_id')} />
        <Input placeholder="搜索用户 ID" value={searchUserId} onChange={(e) => setSearchUserId(e.target.value)} style={{ width: 140 }} allowClear disabled={!!searchParams.get('user_id')} />
        <Input placeholder="搜索 API Key" value={searchApiKey} onChange={(e) => setSearchApiKey(e.target.value)} style={{ width: 220 }} allowClear disabled={!!searchParams.get('user_id')} />
        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>搜索</Button>
        <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
      </div>

      <Modal title="修改 Key 限流与并发" open={!!limitKey} onCancel={()=>{if(!savingLimits)setLimitKey(null);}} confirmLoading={savingLimits} okButtonProps={{disabled:!validKeyLimits(editLimits)}} onOk={async()=>{setSavingLimits(true);try{const result=await apiKeyAPI.saveLimits(limitKey.id,editLimits);if(result.code!==0)throw new Error(result.message);setLimitKey(null);fetchData(pagination.current,pagination.pageSize,searchEmail,searchUserId,searchApiKey,sortBy,sortOrder);message.success('限流配置已更新');}catch(error){message.error(error.response?.data?.message || error.message);}finally{setSavingLimits(false);}}}>
        <KeyLimitField value={editLimits} onChange={setEditLimits} />
      </Modal>
      <KeyGroupsModal open={groupsOpen} onClose={()=>setGroupsOpen(false)} api={apiKeyAPI} />
      <Button onClick={()=>setGroupsOpen(true)} style={{marginBottom:12,marginRight:8}}>管理渠道分组</Button>
      <Button style={{marginBottom:12}} onClick={()=>{setActivityKey(null);setActivityOpen(true);}}>全部 Key 请求日志</Button>
      <KeyBatchBar ids={selectedKeys} onSelectionChange={setSelectedKeys} execute={apiKeyAPI.batch} onDone={()=>fetchData(pagination.current,pagination.pageSize,searchEmail,searchUserId,searchApiKey,sortBy,sortOrder)} />
      <KeyBudgetModal row={budgetKey} api={apiKeyAPI} onClose={()=>setBudgetKey(null)} onSaved={()=>fetchData(pagination.current,pagination.pageSize,searchEmail,searchUserId,searchApiKey,sortBy,sortOrder)} />
      <KeyActivityModal open={activityOpen} keyRecord={activityKey} load={apiKeyAPI.activity} onClose={()=>setActivityOpen(false)} />
      {/* 表格容器 */}
      <div className="table-container">
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          rowSelection={{selectedRowKeys:selectedKeys,onChange:setSelectedKeys,preserveSelectedRowKeys:true}}
          loading={loading}
          scroll={{ x: 1400, y: 600 }}
          pagination={{ ...pagination, showSizeChanger: true, showQuickJumper: true, showTotal: (total) => `共 ${total} 条` }}
          onChange={handleTableChange}
          size="small"
        />
      </div>
    </div>
  );
};

export default function APIKeysPage() {
  return <AntApp style={{ height: '100%' }}><APIKeys /></AntApp>;
}
